import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getSessionId } from '../App';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { ArrowLeft, Sun, Moon, Mail, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Document, Page, pdfjs } from 'react-pdf';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function ReaderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readingMode, setReadingMode] = useState('day'); // day, night, sepia
  const [fontSize, setFontSize] = useState(18);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [tracked, setTracked] = useState(false);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const response = await axios.get(`${API}/books/${id}`);
        setBook(response.data);
      } catch (error) {
        console.error('Failed to fetch book:', error);
        toast.error('Book not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
  }, [id, navigate]);

  useEffect(() => {
    // Track read once
    if (book && !tracked) {
      const trackRead = async () => {
        try {
          await axios.post(`${API}/track/read`, {
            type: 'read',
            book_id: book.id,
            session_id: getSessionId()
          });
          setTracked(true);
        } catch (error) {
          console.error('Failed to track read:', error);
        }
      };

      trackRead();
    }
  }, [book, tracked]);

  const handleRequestCopy = () => {
    if (!book) return;

    const subject = encodeURIComponent(`Request: ${book.title}`);
    const body = encodeURIComponent(
      `Hi,\n\nI'd like to request a copy of "${book.title}" by ${book.author}.\n\nThank you!`
    );

    window.location.href = `mailto:jeansky@protonmail.com?subject=${subject}&body=${body}`;
  };

  const getModeColors = () => {
    switch (readingMode) {
      case 'night':
        return { bg: '#2C3E50', text: '#E8DCC8' };
      case 'sepia':
        return { bg: '#F4ECD8', text: '#5C4A3A' };
      default:
        return { bg: '#FDFCFB', text: '#1A4D5C' };
    }
  };

  const colors = getModeColors();

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(numPages, prev + 1));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: colors.bg }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0CAAA6]"></div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen transition-colors duration-500"
      style={{ background: colors.bg, color: colors.text }}
    >
      {/* Top Navigation */}
      <nav 
        className="sticky top-0 z-50 backdrop-blur-md border-b"
        style={{ 
          background: `${colors.bg}dd`,
          borderColor: readingMode === 'night' ? '#ffffff22' : '#0CAAA633'
        }}
      >
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(`/book/${id}`)}
            style={{ color: colors.text }}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <h1 className="text-sm font-medium truncate max-w-xs">{book.title}</h1>

          <div className="flex items-center gap-2">
            {/* Font Size Control */}
            <div className="flex items-center gap-2 px-3 py-1 rounded-lg" style={{ background: `${colors.text}11` }}>
              <span className="text-xs">Aa</span>
              <Slider
                value={[fontSize]}
                onValueChange={(value) => setFontSize(value[0])}
                min={14}
                max={24}
                step={2}
                className="w-20"
              />
            </div>

            {/* Reading Mode Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const modes = ['day', 'night', 'sepia'];
                const currentIndex = modes.indexOf(readingMode);
                setReadingMode(modes[(currentIndex + 1) % modes.length]);
              }}
              style={{ color: colors.text }}
              data-testid="theme-toggle"
            >
              {readingMode === 'night' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </nav>

      {/* Reading Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {book.file_type === 'pdf' ? (
          <div className="flex flex-col items-center space-y-4">
            <Document
              file={`${process.env.REACT_APP_BACKEND_URL}/api/books/${book.id}/content`}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0CAAA6]"></div>
                </div>
              }
            >
              <Page 
                pageNumber={pageNumber} 
                renderTextLayer={true}
                renderAnnotationLayer={true}
                className="shadow-2xl"
                width={Math.min(800, window.innerWidth - 32)}
              />
            </Document>
          </div>
        ) : (
          <div className="prose prose-lg max-w-none" style={{ color: colors.text, fontSize: `${fontSize}px` }}>
            <p className="text-center text-[#7A8286] py-20">
              EPUB reader coming soon. For now, please request a copy via email.
            </p>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      {book.file_type === 'pdf' && numPages && (
        <nav 
          className="fixed bottom-0 left-0 right-0 backdrop-blur-md border-t"
          style={{ 
            background: `${colors.bg}dd`,
            borderColor: readingMode === 'night' ? '#ffffff22' : '#0CAAA633'
          }}
        >
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              style={{ color: colors.text }}
              data-testid="prev-page-button"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>

            <div className="flex items-center gap-4">
              <div className="w-64">
                <Slider
                  value={[pageNumber]}
                  onValueChange={(value) => setPageNumber(value[0])}
                  min={1}
                  max={numPages}
                  step={1}
                />
              </div>
              <span className="text-sm font-medium whitespace-nowrap" data-testid="page-info">
                {pageNumber} / {numPages}
              </span>
            </div>

            <Button
              variant="ghost"
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              style={{ color: colors.text }}
              data-testid="next-page-button"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </nav>
      )}

      {/* Floating Request Button */}
      <Button
        onClick={handleRequestCopy}
        className="fixed bottom-24 right-6 h-14 w-14 rounded-full shadow-2xl bg-[#0CAAA6] hover:bg-[#0A8883] text-white"
        data-testid="floating-request-button"
      >
        <Mail className="w-6 h-6" />
      </Button>
    </div>
  );
}